import { IReactionDisposer, autorun } from 'mobx';

import apiActivityTracker from '@/api/apiActivityTracker';
import type { FilesListType } from './interfaces/common';
import { IDLE_THRESHOLD_MS } from './options';

type RefreshActivityMode = 'paused' | 'active' | 'idle';

type RefreshCadence = 'paused' | 'passive' | 'active' | 'active-immediate' | 'idle';

interface RefreshActivityManager {
    syncRefreshCadence(cadence: RefreshCadence): void;
}

export interface RefreshActivityUploadStore {
    isUploading: boolean;
    isLoading: boolean;
}

const HUMAN_INPUT_EVENTS = [
    'pointerdown',
    'keydown',
    'touchstart',
    'click',
] as const;

const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };
const LISTENER_CAPTURE = true;

/** Re-evaluate refresh cadence on this interval so idle transitions are not missed. */
const RECOMPUTE_INTERVAL_MS = 5_000;

/**
 * Drives files-list refresh cadence from tab visibility, user input,
 * and in-flight API/upload activity while a files view is mounted.
 */
class RefreshActivityController {
    private readonly managers: Record<FilesListType, RefreshActivityManager>;

    private readonly uploadFilesStore: RefreshActivityUploadStore;

    private activeFilesListType: FilesListType | null = null;

    private currentMode: RefreshActivityMode | null = null;

    private lastInteractionAt = 0;

    private recomputeIntervalId: ReturnType<typeof setInterval> | null = null;

    private activityDisposer: IReactionDisposer | null = null;

    constructor(
        managers: Record<FilesListType, RefreshActivityManager>,
        uploadFilesStore: RefreshActivityUploadStore,
    ) {
        this.managers = managers;
        this.uploadFilesStore = uploadFilesStore;
    }

    start(filesListType: FilesListType): void {
        if (this.activeFilesListType) {
            this.stop();
        }

        this.activeFilesListType = filesListType;
        this.lastInteractionAt = Date.now();
        this.currentMode = null;

        HUMAN_INPUT_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, this.handleUserInteraction, LISTENER_OPTIONS);
        });
        document.addEventListener('visibilitychange', this.handlePageStateChange);
        window.addEventListener('focus', this.handlePageStateChange);
        window.addEventListener('blur', this.handlePageStateChange);

        this.activityDisposer = autorun(() => {
            this.hasApiActivity();
            this.recompute();
        });

        this.recomputeIntervalId = setInterval(() => this.recompute(), RECOMPUTE_INTERVAL_MS);
        this.recompute();
    }

    stop(): void {
        if (!this.activeFilesListType) {
            return;
        }

        HUMAN_INPUT_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, this.handleUserInteraction, LISTENER_CAPTURE);
        });
        document.removeEventListener('visibilitychange', this.handlePageStateChange);
        window.removeEventListener('focus', this.handlePageStateChange);
        window.removeEventListener('blur', this.handlePageStateChange);

        if (this.recomputeIntervalId !== null) {
            clearInterval(this.recomputeIntervalId);
            this.recomputeIntervalId = null;
        }

        if (this.activityDisposer) {
            this.activityDisposer();
            this.activityDisposer = null;
        }

        const { activeFilesListType } = this;
        this.managers[activeFilesListType].syncRefreshCadence('passive');

        this.activeFilesListType = null;
        this.currentMode = null;
    }

    private forEachBackground(
        activeFilesListType: FilesListType,
        callback: (manager: RefreshActivityManager) => void,
    ): void {
        (Object.entries(this.managers) as [FilesListType, RefreshActivityManager][]).forEach(
            ([filesListType, manager]) => {
                if (filesListType !== activeFilesListType) {
                    callback(manager);
                }
            },
        );
    }

    private handleUserInteraction = (): void => {
        this.lastInteractionAt = Date.now();
        this.recompute();
    };

    private handlePageStateChange = (): void => {
        this.recompute();
    };

    private interactedWithinThreshold(): boolean {
        return Date.now() - this.lastInteractionAt < IDLE_THRESHOLD_MS;
    }

    private hasApiActivity(): boolean {
        return apiActivityTracker.isActive
            || this.uploadFilesStore.isUploading
            || this.uploadFilesStore.isLoading;
    }

    private recompute(): void {
        if (!this.activeFilesListType) {
            return;
        }

        const nextMode = this.resolveMode();
        if (nextMode === this.currentMode) {
            return;
        }

        const previousMode = this.currentMode;
        this.currentMode = nextMode;
        this.applyMode(nextMode, previousMode);
    }

    private resolveMode(): RefreshActivityMode {
        if (document.visibilityState !== 'visible' || !document.hasFocus()) {
            return 'paused';
        }

        if (this.interactedWithinThreshold() || this.hasApiActivity()) {
            return 'active';
        }

        return 'idle';
    }

    private applyMode(mode: RefreshActivityMode, previousMode: RefreshActivityMode | null): void {
        const { activeFilesListType } = this;
        if (!activeFilesListType) {
            return;
        }

        if (mode === 'paused') {
            (Object.values(this.managers) as RefreshActivityManager[]).forEach((manager) => {
                manager.syncRefreshCadence('paused');
            });
            return;
        }

        const shouldRefreshImmediately = previousMode === 'paused' || previousMode === 'idle';

        if (mode === 'active') {
            this.forEachBackground(activeFilesListType, (manager) => manager.syncRefreshCadence('passive'));
            this.managers[activeFilesListType].syncRefreshCadence(
                shouldRefreshImmediately ? 'active-immediate' : 'active',
            );
            return;
        }

        this.forEachBackground(activeFilesListType, (manager) => manager.syncRefreshCadence('paused'));
        this.managers[activeFilesListType].syncRefreshCadence('idle');
    }
}

export default RefreshActivityController;
